import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, MessageSquare, MapPin } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { InstagramIcon, LinkedinIcon, YoutubeIcon, FacebookIcon } from "@/components/SocialIcons";
import { FOOTER } from "@/constants/testIds";
import { OFFERING_CATEGORIES } from "@/data/courses";

export default function Footer({
  // Kept unchanged everywhere by default; Layout overrides this to a
  // tighter value on the homepage, where the final CTA section right
  // above the footer already carries its own bottom padding.
  topPaddingClass = "pt-16 lg:pt-20",
}) {
  return (
    <footer data-testid={FOOTER.root} className="relative bg-bg-dark text-white">
      <div className="absolute inset-0 bg-grid-dark opacity-30 pointer-events-none" aria-hidden />
      <div className={`relative mx-auto max-w-[1440px] px-5 lg:px-10 ${topPaddingClass} pb-16 lg:pb-20`}>
        {/* Top CTA strip */}
        <div className="grid lg:grid-cols-12 gap-8 items-end pb-12 border-b border-white/10">
          <div className="lg:col-span-7">
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-[var(--cyan)]">
              Talk to a training advisor
            </p>
            <h2 className="font-display text-2xl md:text-3xl lg:text-[34px] font-bold mt-3 leading-[1.05] text-balance">
              Need help choosing a course?
              <br />
              <span style={{ color: "var(--cyan)" }}>Our team will help you choose the right path.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
            <Link
              to="/contact"
              data-testid={FOOTER.cta}
              className="inline-flex items-center gap-2 rounded-md bg-[var(--cyan)] px-5 py-3 text-sm font-bold text-[var(--royal-deep)] hover:-translate-y-0.5 transition-transform"
            >
              Get Course Guidance <ArrowUpRight size={14} />
            </Link>
            <Link
              to="/corporate-training"
              className="inline-flex items-center gap-2 rounded-md border border-white/20 px-5 py-3 text-sm font-semibold text-white hover:bg-white/5"
            >
              Plan Corporate Training
            </Link>
          </div>
        </div>

        {/* Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pt-12">
          <div className="lg:col-span-4">
            <BrandLogo />
            <p className="mt-4 text-sm leading-relaxed text-white/70 max-w-sm">
              ZaranTech is a global EdTech company offering practical IT training in SAP, Workday,
              AI tools, and enterprise technologies ,  for professionals and corporate teams.
            </p>
            {/* <div className="mt-6 flex flex-wrap gap-2">
              {["SAP", "Workday", "AI", "Copilot", "Claude"].map((t) => (
                <span
                  key={t}
                  className="rounded-full border border-white/15 px-3 py-1 text-[11px] font-mono-ui tracking-wider text-white/80"
                >
                  {t}
                </span>
              ))}
            </div> */}

            <div className="mt-6 space-y-4">
              {[
                {
                  label: "Head Office (USA)",
                  address: "5550 Wild Rose Ln #400, West Des Moines, IA 50266, United States",
                  href: "https://share.google/dMsoyTEIqBc8KLwXk",
                },
                {
                  label: "Offshore Office (IND)",
                  address: "Arun Arcade, 170, 5th Main Rd, Amarjyoti Layout, Domlur, Bengaluru, Karnataka 560071, India",
                  href: "https://share.google/n2xbMPkRMG0iUgtrb",
                },
              ].map((office) => (
                <div key={office.label} className="flex items-start gap-3">
                  <MapPin size={16} className="mt-0.5 shrink-0 text-[var(--cyan)]" />
                  <div>
                    <p className="font-mono-ui text-[11px] uppercase tracking-[0.18em] text-white/50">
                      {office.label}
                    </p>
                    <a
                      href={office.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 block text-sm leading-relaxed text-white/80 hover:text-[var(--cyan)] max-w-sm"
                    >
                      {office.address}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Link columns: five equal columns on large screens. */}
          <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-6">
          <div>
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-white/50 mb-4">
              Pages
            </p>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/" className="text-white/85 hover:text-[var(--cyan)]">
                  Home
                </Link>
              </li>

              <li>
                <Link to="/about-us" className="text-white/85 hover:text-[var(--cyan)]">
                  About Us
                </Link>
              </li>

              <li>
                <Link to="/courses" className="text-white/85 hover:text-[var(--cyan)]">
                  Courses
                </Link>
              </li>

              <li>
                <a
                  href="https://zarantech.teachable.com/l/products"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/85 hover:text-[var(--cyan)]"
                >
                  Video Course
                </a>
              </li>

              <li>
                <Link to="/corporate-training" className="text-white/85 hover:text-[var(--cyan)]">
                  Corporate Training
                </Link>
              </li>

              <li>
                <Link to="/leadership" className="text-white/85 hover:text-[var(--cyan)]">
                  AI Leadership Training
                </Link>
              </li>

              <li>
                <Link to="/graduate-training" className="text-white/85 hover:text-[var(--cyan)]">
                  Graduate Training
                </Link>
              </li>

              <li>
                <Link to="/resources" className="text-white/85 hover:text-[var(--cyan)]">
                  Resources
                </Link>
              </li>

              <li>
                <Link to="/contact" className="text-white/85 hover:text-[var(--cyan)]">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-white/50 mb-4">
              Resources
            </p>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/resources" className="text-white/85 hover:text-[var(--cyan)]">
                  Guides/EBooks
                </Link>
              </li>

              <li>
                <Link to="/live-sessions" className="text-white/85 hover:text-[var(--cyan)]">
                  Live Sessions
                </Link>
              </li>

              <li>
                <Link to="/blog" className="text-white/85 hover:text-[var(--cyan)]">
                  Blogs
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-white/50 mb-4">
              Get Involved
            </p>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/careers" className="text-white/85 hover:text-[var(--cyan)]">
                  Career Page
                </Link>
              </li>
              <li>
                <Link to="/become-a-trainer" className="text-white/85 hover:text-[var(--cyan)]">
                  Become a Trainer
                </Link>
              </li>
              <li>
                <Link to="/refer-a-friend" className="text-white/85 hover:text-[var(--cyan)]">
                  Refer a Friend
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-white/50 mb-4">
              Course Categories
            </p>
            <ul className="space-y-2.5 text-sm">
              {OFFERING_CATEGORIES.map((c) => (
                <li key={c.id}>
                  <Link to={c.link} className="text-white/85 hover:text-[var(--cyan)]">
                    {c.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-white/50 mb-4">
              Get in touch
            </p>
            <ul className="space-y-3 text-sm text-white/80">
              <li className="flex items-center gap-2">
                <Mail size={14} className="text-[var(--cyan)]" />
                <a href="mailto:sales@zarantech.com" className="hover:text-[var(--cyan)]">
                  sales@zarantech.com
                </a>
              </li>
              <li className="flex items-center gap-2">
                <MessageSquare size={14} className="text-[var(--cyan)]" />
                <a
                  href="https://wa.me/15152007716"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[var(--cyan)]"
                >
                  +1 (515) 200 7716
                </a>
              </li>
              <li className="text-xs text-white/50 pt-1">
                Global delivery · Live + Self-paced · Customized Corporate Programs
              </li>
            </ul>
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-white/50 mt-6 mb-3">
              Follow us
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/zarantech/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="text-white/70 hover:text-[var(--cyan)] transition-colors"
              >
                <InstagramIcon className="h-7 w-7" />
              </a>
              <a
                href="https://www.linkedin.com/company/zarantech-llc"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="text-white/70 hover:text-[var(--cyan)] transition-colors"
              >
                <LinkedinIcon className="h-7 w-7" />
              </a>
              <a
                href="https://www.youtube.com/@zarantechdotcom"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="text-white/70 hover:text-[var(--cyan)] transition-colors"
              >
                <YoutubeIcon className="h-7 w-7" />
              </a>
              <a
                href="https://www.facebook.com/ZaranTech/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="text-white/70 hover:text-[var(--cyan)] transition-colors"
              >
                <FacebookIcon className="h-7 w-7" />
              </a>
            </div>
          </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-xs text-white/55">
            © {new Date().getFullYear()} ZaranTech. All rights reserved.
          </p>
          <ul className="flex flex-wrap gap-5 text-xs text-white/65">
            <li><Link to="/policies/privacy-policy" className="hover:text-[var(--cyan)]">Privacy Policy</Link></li>
            <li><Link to="/policies/terms-and-conditions" className="hover:text-[var(--cyan)]">Terms and Conditions</Link></li>
            <li><Link to="/policies/refund-policy" className="hover:text-[var(--cyan)]">Refund Policy</Link></li>
            <li><Link to="/verify-certificate" className="hover:text-[var(--cyan)]">Verify Certificate</Link></li>
          </ul>
        </div>
      </div>
    </footer>
  );
}
